import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUser } from '../../../../../common/context/User'
import { hasOrganizationAction } from '../../../../../common/utils/user'
import {
  ArrowLeftIcon,
  XIcon,
  Dialog,
  Flex,
  IconButton,
  ScrollArea,
  useMediaQuery,
} from '@buffer-mono/popcorn'
import { useQuery } from '@apollo/client/react'
import { useSplitEnabled } from '@buffer-mono/features'
import clsx from 'clsx'

import { GetConnectableServices } from './query'
import { Service } from '../../../../../gql/graphql'
import type { ConnectableService } from '../../../../../gql/graphql'

import ChannelStoreFrontItem from './ChannelStoreFrontItem'
import ServiceDetails from './ServiceDetails'
import ChannelStoreFrontSkeleton from './ChannelStoreFrontSkeleton'
import { sanitizeNullableArray } from '../../../../../helpers/typeGuards'
import {
  setStartConnection,
  resetChannelConnectionsState,
} from '../../../store/channelConnectionsReducer'
import useScrollPositionThreshold from '../../../../../common/hooks/useScrollPositionThreshold'

import styles from './ChannelStoreFront.module.css'
import {
  ensureEmptyCookieState,
  serviceRequiresFurtherConfiguration,
} from '../../utils'
import type { Service as ServiceLegacy } from '../../types'
import RequestAChannel from './components/RequestAChannel/RequestAChannel'
import trackChannelStorefrontViewed from '../../../../../tracking/ChannelStorefrontDialog/trackChannelStorefrontViewed'
import type { OrchestratorRootState } from '../../../../../common/events/types'
import trackChannelStorefrontMoreDetailsClicked from '../../../../../tracking/ChannelStorefrontDialog/trackChannelStorefrontMoreDetailsClicked'
import {
  getStartPageUrl,
  redirectToUrl,
} from '../../../../../common/utils/urls'

function ChannelList({
  connectableServices,
  handleOpenServiceDetails,
  handleConnectService,
  isLoading,
  canAddChannels,
  isSettingPostingGoal = false,
}: {
  connectableServices: ConnectableService[]
  handleOpenServiceDetails: (connectableService: ConnectableService) => void
  handleConnectService: (connectableService: Service) => void
  isLoading: boolean
  canAddChannels: boolean
  isSettingPostingGoal: boolean
}): JSX.Element {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  )
  const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(
    null,
  )
  const isPastThreshold = useScrollPositionThreshold({
    element: scrollElement,
    threshold: 1,
  })

  const isMobile = useMediaQuery('(width < 641px)')

  const wrapperHeight = useMemo(() => {
    return isMobile
      ? `calc(${window.innerHeight}px - ${headerElement?.clientHeight}px)`
      : undefined
  }, [isMobile, headerElement])

  if (isLoading) {
    return <ChannelStoreFrontSkeleton />
  }

  const title = isSettingPostingGoal
    ? 'Connect a Channel to Set a Posting Goal'
    : 'Connect a New Channel'

  const description = 'Pick a channel to get started.'

  return (
    <Flex direction="column" align="center" className={styles.content}>
      <Dialog.Header
        ref={(newRef: HTMLDivElement): void => setHeaderElement(newRef)}
        className={styles.header}
      >
        <Dialog.Title className={styles.title}>{title}</Dialog.Title>
        {isSettingPostingGoal && (
          <Dialog.Description>{description}</Dialog.Description>
        )}
      </Dialog.Header>
      {isPastThreshold && <Dialog.Separator />}
      <div
        className={clsx(styles.wrapper, styles.scrollWrapper)}
        style={{ height: wrapperHeight }}
      >
        <ScrollArea
          ref={(newRef: HTMLDivElement): void => setScrollElement(newRef)}
        >
          <div className={clsx(styles.innerWrapper)}>
            {connectableServices.length > 0 &&
              connectableServices.map((serviceData: ConnectableService) => (
                <ChannelStoreFrontItem
                  key={serviceData.service}
                  connectableService={serviceData}
                  handleOpenServiceDetails={handleOpenServiceDetails}
                  handleConnectService={handleConnectService}
                  canAddChannels={canAddChannels}
                />
              ))}
            <RequestAChannel />
          </div>
        </ScrollArea>
      </div>
    </Flex>
  )
}
function ChannelStoreFront(): JSX.Element | null {
  const [selectedService, setSelectedService] =
    useState<ConnectableService | null>(null)
  const user = useUser()
  const dispatch = useDispatch()
  const {
    cta,
    skipCelebrationModal,
    prefillDataAfterConnection,
    selectedService: selectedServiceState,
    isSettingPostingGoal = false,
  } = useSelector((state: OrchestratorRootState) => state.channelConnections)
  const organizationId = user.currentOrganization?.id
  const { isEnabled: whatsappEnabled } = useSplitEnabled(
    'whatsapp-channel-connection',
    { splitKey: organizationId },
  )
  const whatsappAvailable =
    Boolean(organizationId) && whatsappEnabled && !isSettingPostingGoal
  // Hidden once the sunset is announced. core-api drops it from the list too;
  // this also covers a list fetched before that deploy.
  const { isEnabled: startPageSunsetAnnounced } = useSplitEnabled(
    'start-page-sunset-announcement',
    { splitKey: organizationId },
  )
  const startPageAvailable = !startPageSunsetAnnounced
  const canAddChannels = hasOrganizationAction(user, 'manageChannels')

  const {
    data,
    error,
    loading: isLoading,
  } = useQuery(GetConnectableServices, {
    variables: {
      input: { organization: organizationId },
    },
  })

  useEffect(() => {
    if (
      error ||
      data?.connectableServices.__typename === 'ConnectableServicesError'
    ) {
      return
    }

    if (selectedServiceState) {
      const selectedService = data?.connectableServices.services.find(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore 2 enums with the same values
        (service) => service.service === selectedServiceState,
      )

      if (selectedService) {
        setSelectedService(selectedService)
      }
    }
  }, [data, error, selectedServiceState])

  useEffect(() => {
    const payload = {
      cta,
    }
    trackChannelStorefrontViewed({
      user,
      payload,
    })
  }, [cta, user])

  if (
    error ||
    data?.connectableServices.__typename === 'ConnectableServicesError'
  ) {
    return null
  }

  const connectableServices = sanitizeNullableArray(
    data?.connectableServices.services,
  ).filter(
    (service) =>
      (service.service !== Service.Whatsapp || whatsappAvailable) &&
      (service.service !== Service.StartPage || startPageAvailable),
  )
  const visibleSelectedService =
    (selectedService?.service === Service.Whatsapp && !whatsappAvailable) ||
    (selectedService?.service === Service.StartPage && !startPageAvailable)
      ? null
      : selectedService

  function handleClose(): void {
    dispatch(resetChannelConnectionsState({}))
  }

  function handleOpenServiceDetails(selectedService: ConnectableService): void {
    setSelectedService(selectedService)
    trackChannelStorefrontMoreDetailsClicked({
      user,
      payload: {
        cta,
        service: selectedService.service,
      },
    })
  }

  function handleBackClick(): void {
    setSelectedService(null)
  }

  function handleConnectService(service: Service): void {
    if (service === Service.StartPage.toLocaleLowerCase()) {
      const startPageOnboardingUrl = getStartPageUrl('onboarding')
      redirectToUrl(startPageOnboardingUrl)
      return
    }

    ensureEmptyCookieState()

    dispatch(
      setStartConnection({
        selectedService: service,
        destinationUrl: window.location.href,
        cta,
        skipCelebrationModal,
        prefillDataAfterConnection,
        serviceRequiresFurtherConfiguration:
          serviceRequiresFurtherConfiguration({
            service: service as unknown as ServiceLegacy,
            isRefreshingConnection: false,
          }),
      }),
    )
    setSelectedService(null)
  }

  return (
    <>
      <div>
        <Dialog.Close>
          <IconButton
            label="Close"
            variant="secondary"
            size="medium"
            className={styles.closeButton}
            onClick={handleClose}
          >
            <XIcon />
          </IconButton>
        </Dialog.Close>
        {visibleSelectedService && (
          <IconButton
            className={styles.backIcon}
            variant="tertiary"
            label="Go back to Channels List"
            onClick={handleBackClick}
            size="medium"
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
      </div>
      {!visibleSelectedService && (
        <ChannelList
          connectableServices={connectableServices}
          handleOpenServiceDetails={handleOpenServiceDetails}
          handleConnectService={handleConnectService}
          isLoading={isLoading}
          canAddChannels={canAddChannels}
          isSettingPostingGoal={isSettingPostingGoal}
        />
      )}
      {visibleSelectedService && (
        <ServiceDetails
          connectableService={visibleSelectedService}
          handleConnectService={handleConnectService}
          canAddChannels={canAddChannels}
        />
      )}
    </>
  )
}

export default ChannelStoreFront
